(() => {
  const componentName = 'vl-peek-card-slider';

  if (customElements.get(componentName)) return;

  class VLPeekCardSlider extends HTMLElement {
    constructor() {
      super();

      this.activeIndex = 0;
      this.scrollFrame = null;
      this.isInitialized = false;
      this.resizeObserver = null;

      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.updateFromPosition = this.updateFromPosition.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.track = this.querySelector('[data-vl-peek-card-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-vl-peek-card-slider-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-vl-peek-card-slider-dot]'));
      this.previousButton = this.querySelector('[data-vl-peek-card-slider-previous]');
      this.nextButton = this.querySelector('[data-vl-peek-card-slider-next]');

      if (!this.track || !this.slides.length) return;

      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('keydown', this.handleKeydown);
      this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateFromPosition);
        this.resizeObserver.observe(this.track);
      }

      this.isInitialized = true;
      this.updateUI();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.track?.removeEventListener('scroll', this.handleScroll);
      this.track?.removeEventListener('keydown', this.handleKeydown);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.resizeObserver?.disconnect();

      if (this.scrollFrame !== null) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      this.isInitialized = false;
    }

    handlePrevious() {
      this.goTo(this.activeIndex - 1);
    }

    handleNext() {
      this.goTo(this.activeIndex + 1);
    }

    handleDotClick(event) {
      this.goTo(Number(event.currentTarget.dataset.vlPeekCardSliderIndex));
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePrevious();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNext();
      }
    }

    handleScroll() {
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(this.updateFromPosition);
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('[data-vl-peek-card-slider-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      this.goTo(Number(selectedSlide.dataset.vlPeekCardSliderIndex), false);
    }

    updateFromPosition() {
      this.scrollFrame = null;

      const maxIndex = this.getMaxIndex();
      const maxScrollLeft = this.getScrollTarget(maxIndex);

      if (this.track.scrollLeft > maxScrollLeft + 1) {
        this.track.scrollTo({ left: maxScrollLeft, behavior: 'auto' });
      }

      const trackLeft = this.track.getBoundingClientRect().left + this.getTrackInset();
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.slice(0, maxIndex + 1).forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex === this.activeIndex) {
        this.updateUI();
        return;
      }

      this.activeIndex = nearestIndex;
      this.updateUI();
    }

    goTo(index, smooth = true) {
      const nextIndex = Math.max(0, Math.min(index, this.getMaxIndex()));
      const targetSlide = this.slides[nextIndex];
      if (!targetSlide) return;

      this.activeIndex = nextIndex;
      this.track.scrollTo({
        left: this.getScrollTarget(nextIndex),
        behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
      });
      this.updateUI();
    }

    getTrackInset() {
      return Number.parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    }

    getMaxIndex() {
      if (this.slides.length <= 1) return 0;

      const trackWidth = this.track.getBoundingClientRect().width;
      const availableWidth = Math.max(0, trackWidth - this.getTrackInset());
      const slideWidth = this.slides[0].getBoundingClientRect().width;
      const secondSlide = this.slides[1];
      const slideStep = secondSlide
        ? secondSlide.getBoundingClientRect().left - this.slides[0].getBoundingClientRect().left
        : slideWidth;

      if (slideWidth <= 0 || slideStep <= 0) return this.slides.length - 1;

      const gap = Math.max(0, slideStep - slideWidth);
      const fullyVisibleSlides = Math.max(1, Math.floor((availableWidth + gap) / slideStep));

      return Math.max(0, this.slides.length - fullyVisibleSlides);
    }

    getScrollTarget(index) {
      const targetSlide = this.slides[index];
      if (!targetSlide) return 0;

      return (
        this.track.scrollLeft +
        targetSlide.getBoundingClientRect().left -
        this.track.getBoundingClientRect().left -
        this.getTrackInset()
      );
    }

    updateUI() {
      const maxIndex = this.getMaxIndex();

      if (this.activeIndex > maxIndex) this.activeIndex = maxIndex;

      this.classList.toggle('is-static', maxIndex === 0);

      this.dots.forEach((dot, index) => {
        const isActive = index === this.activeIndex;
        const isUnavailable = index > maxIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
        dot.disabled = isUnavailable;
        dot.hidden = isUnavailable;
      });

      this.updateControls();
    }

    updateControls() {
      const maxIndex = this.getMaxIndex();

      if (this.previousButton) this.previousButton.disabled = this.activeIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex >= maxIndex;
    }
  }

  customElements.define(componentName, VLPeekCardSlider);
})();
